// "Did you mean?", for the address a shape check cannot fault.
//
// The block in validate.ts refuses a string that is not an address. It has
// nothing to say about gabe@gmial.com, which is a well-formed address at a
// domain that exists, passes every rule this package has, and delivers to
// nobody. That is the email failure that actually costs a lead: the person has
// walked away, the follow-up bounces into a mailbox nobody reads weeks after
// the show, and the client paid for a name that was never reachable. A block
// cannot reach it and neither can anything downstream.
//
// WHY THIS SUGGESTS AND WILL NEVER CORRECT.
//
// The temptation is obvious, and it should be turned down every time it comes
// back. A typed "gmial.com" is a mistake often enough that writing "gmail.com"
// over the top of it would be right on nearly every occasion it happened. The
// occasions it was wrong would be the invisible ones: a real address at a real
// domain that happens to sit one keystroke away from a large one, replaced with
// somebody else's, with no party anywhere in the system aware that a character
// was changed. A rare typo fixed quietly is not a rare typo any more, it is a
// rare wrong address that nobody can find, in a record that says a person
// supplied it. The person at the tablet is the only party who can tell which of
// the two cases this is, so the answer has to be theirs. What the interface
// owes them in return is that saying yes costs one tap, which is Field.tsx's
// half of this.
//
// WHAT IT WILL AND WILL NOT GUESS AT.
//
// A closed list of domains, matched by edit distance, and three rules that keep
// it quiet:
//
//   A domain already on the list is not a typo of itself, so nothing is said.
//
//   A domain that is a leading fragment of a candidate is somebody part way
//   through typing rather than somebody who mistyped. "gmail.co" is suppressed
//   for that reason, and the cost of the rule is that a person who genuinely
//   meant to type "gmail.com" and stopped one character early gets no
//   suggestion. The read-back step is the backstop for that one, and a
//   suggestion appearing and disappearing under a finger on every other
//   keystroke would be paid for on every address anybody ever types.
//
//   Two candidates equally close is not a suggestion, it is a guess between
//   two, so nothing is said. "mae.com" is one keystroke from both me.com and
//   mac.com and there is no reading of it that is better than the other, which
//   is the case that keeps this rule honest as the list grows.
//
// The list is short and deliberately unambitious. It is the free mail providers
// a British trade show sees, which is where nearly all of this lands: a
// mistyped company domain is unguessable, because there is no list of every
// firm in the hall and inventing one would suggest a domain at random.

/**
 * The domains worth guessing at, lower case.
 *
 * Ordinary consumer providers, plus the British ones an exhibition crowd still
 * uses. A domain earns a place here by being common enough that a near miss is
 * more likely to be a typo of it than a real address somewhere else, which is a
 * higher bar than "we have heard of it".
 */
export const COMMON_EMAIL_DOMAINS: readonly string[] = [
  "gmail.com",
  "googlemail.com",
  "outlook.com",
  "hotmail.com",
  "hotmail.co.uk",
  "live.co.uk",
  "msn.com",
  "yahoo.com",
  "yahoo.co.uk",
  "icloud.com",
  "me.com",
  "mac.com",
  "aol.com",
  "btinternet.com",
  "sky.com",
  "virginmedia.com",
  "talktalk.net",
  "ntlworld.com",
  "blueyonder.co.uk",
  "protonmail.com",
];

/**
 * Optimal string alignment: insertions, deletions, substitutions and swapped
 * adjacent characters, each costing one.
 *
 * The transposition is the reason this is not plain Levenshtein. "gmial" is one
 * mistake by any account a person would give of it, and counting it as two
 * would put it outside the threshold for a domain of that length.
 */
function editDistance(a: string, b: string): number {
  const width = b.length + 1;
  const grid = new Array<number>((a.length + 1) * width).fill(0);
  const at = (i: number, j: number): number => grid[i * width + j] ?? 0;
  const put = (i: number, j: number, value: number): void => {
    grid[i * width + j] = value;
  };

  for (let i = 0; i <= a.length; i += 1) put(i, 0, i);
  for (let j = 0; j <= b.length; j += 1) put(0, j, j);

  for (let i = 1; i <= a.length; i += 1) {
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      let best = Math.min(at(i - 1, j) + 1, at(i, j - 1) + 1, at(i - 1, j - 1) + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        best = Math.min(best, at(i - 2, j - 2) + 1);
      }
      put(i, j, best);
    }
  }

  return at(a.length, b.length);
}

/**
 * How far wrong a domain may be and still be read as that domain.
 *
 * One for a short domain and two for a long one, because the same two mistakes
 * mean different things at different lengths: two characters out of
 * "btinternet.com" is a person typing quickly, and two characters out of
 * "me.com" is a different domain.
 */
const nearEnough = (candidate: string): number => (candidate.length >= 10 ? 2 : 1);

/**
 * The address this one was probably meant to be, or null.
 *
 * Null is the ordinary answer and covers most of the traffic: an address at a
 * company domain, an address at a provider that is not on the list, and every
 * correctly typed address on it.
 *
 * The local part is returned exactly as it arrived, case included, because the
 * only thing under suspicion here is the domain and rewriting the rest would be
 * changing something nothing has any evidence about. Mail servers are entitled
 * to treat a local part as case-sensitive, and some do.
 */
export function suggestEmail(value: string): string | null {
  const address = value.trim();
  const at = address.lastIndexOf("@");
  if (at <= 0 || at === address.length - 1) return null;

  const local = address.slice(0, at);
  const domain = address.slice(at + 1).toLowerCase();

  if (COMMON_EMAIL_DOMAINS.includes(domain)) return null;

  let best: string | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  let tied = false;

  for (const candidate of COMMON_EMAIL_DOMAINS) {
    // Somebody mid-word, not somebody who mistyped. See the header.
    if (candidate.startsWith(domain)) return null;

    const distance = editDistance(domain, candidate);
    if (distance > nearEnough(candidate)) continue;

    if (distance < bestDistance) {
      best = candidate;
      bestDistance = distance;
      tied = false;
    } else if (distance === bestDistance) {
      tied = true;
    }
  }

  if (best === null || tied) return null;
  return `${local}@${best}`;
}
